import { memo } from 'react';
import { Link } from 'react-router-dom';
import { CHIP, CategoryChip, Countdown, VenueChip } from '../describe/ui';
import { TouchBadge } from '../market/TouchPanel';
import { formatCents, type CatalogItem } from './catalog';
import { panelClick } from './layout';
import TagLabel from './TagLabel';
import { useT } from '../i18n/useT';
import { VolumeCompact, VolumeStat } from '../volume/VolumeCells';

export interface OutcomeTileProps {
  item: CatalogItem;
  /** Search string to append to the market link (e.g. "?ghost=0x…"), '' for none. */
  linkSearch: string;
  /** Open in the inline panel (#1953): highlighted. */
  selected?: boolean;
  /** Opens this outcome in the inline panel; without it the tile is a plain link to the market page. */
  onSelect?: (outcome: number) => void;
}

function OutcomeTileImpl({ item, linkSearch, selected = false, onSelect }: OutcomeTileProps) {
  const t = useT();
  const priced = item.mid !== undefined;
  return (
    <li className={`card flex h-full flex-col transition-colors ${selected ? 'border-accent bg-accent/15' : 'hover:border-accent/60'}`}>
      <Link
        to={`/market/${item.outcome}${linkSearch}`}
        className="flex flex-1 flex-col gap-2 p-3 hover:bg-surface-2/40 sm:p-4"
        onClick={panelClick(item.outcome, onSelect)}
        data-testid="outcome-tile"
        data-outcome-link={item.outcome}
        data-selected={selected ? 'true' : undefined}
        aria-current={selected ? 'true' : undefined}
        aria-label={t('catalog.tile_aria', { id: String(item.outcome), title: item.title })}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted">
              <CategoryChip category={item.category} />
              {item.tagLabel ? <TagLabel tag={item.tagLabel} /> : null}
              {item.touch ? <TouchBadge /> : null}
              <span className={`num ${CHIP}`}>#{item.outcome}</span>
              {item.venue ? <VenueChip venue={item.venue} /> : null}
            </div>
            <div className="mt-1 break-words font-medium leading-snug" data-testid="tile-title" translate="no">
              {item.title}
            </div>
            {item.subtitle ? (
              <div className="mt-0.5 line-clamp-2 break-words text-xs text-muted" data-testid="tile-subtitle" translate="no">
                {item.subtitle}
              </div>
            ) : null}
          </div>
          <VolumeStat volume={item.volume} testId="tile-volume" />
          <div className="shrink-0 text-end" data-testid="tile-price">
            <div className={`num text-lg font-semibold ${priced ? 'text-yes' : 'text-muted'}`}>
              {formatCents(item.mid)}
            </div>
            <div className="max-w-[7rem] truncate text-[11px] text-muted">{priced ? <span translate="no">{item.sides[0]}</span> : t('catalog.no_price')}</div>
          </div>
        </div>
        <div className="mt-auto flex flex-wrap items-center gap-2 text-xs">
          <span className="max-w-[45%] truncate rounded border border-yes/40 px-2 py-0.5 text-yes" translate="no">{item.sides[0]}</span>
          <span className="max-w-[45%] truncate rounded border border-no/40 px-2 py-0.5 text-no" translate="no">{item.sides[1]}</span>
          <VolumeCompact volume={item.volume} testId="tile-volume-compact" />
          {item.resolvesAt ? (
            <span className="ms-auto">
              <Countdown date={item.resolvesAt} label={item.resolvesLabel} />
            </span>
          ) : null}
        </div>
      </Link>
      <details className="border-t border-border/60 px-3 py-1.5 text-xs text-muted sm:px-4">
        <summary className="cursor-pointer select-none hover:text-fg">{t('catalog.details')}</summary>
        <p className="num mt-1 break-all">
          <span className="text-fg/70">{t('describe.raw_name')}</span> {item.name ? <span translate="no">{item.name}</span> : t('catalog.unnamed')}
        </p>
        <p className="num mt-0.5 break-all" data-testid="tile-description" title={item.description || undefined} translate="no">
          {item.description}
        </p>
      </details>
    </li>
  );
}

const OutcomeTile = memo(OutcomeTileImpl);
export default OutcomeTile;
